import { formatChipsShort } from '@polf/shared/locale'

import type {
  PokerPlayerSeatFragment,
  PokerPlayerTableSeatFragment,
} from '../../../__generated__/hooks'

/**
 * Seats are drawn, clock is not yet running (`status=Seated`).
 *
 * This is one of the highest-emotion moments of the poker phase — the
 * room IS buzzing — and the legacy app rendered an Antd Empty here. We
 * give it a real reveal: huge Bord/Sete numerals, tablemates, starting
 * stack, and a one-line cue for what happens next.
 *
 * Once `status` flips to Running the dispatcher swaps in ActiveView and
 * this surface goes away. Players see it for 30–90 seconds; make it
 * worth the screen time.
 */
const SeatedView = ({
  seat,
  tableSeats,
}: {
  seat: PokerPlayerSeatFragment
  tableSeats: ReadonlyArray<PokerPlayerTableSeatFragment>
}) => {
  // Tablemate roster: drop "you" (self) and any eliminated seats (none
  // expected pre-clock, but defensive). Then alphabetical-ish via
  // seatNumber for stable display.
  const others = tableSeats
    .filter((s) => !s.isYou && !s.eliminated)
    .sort((a, b) => a.seatNumber - b.seatNumber)

  return (
    <section className="px-4 pt-4 pb-8 flex flex-col gap-6">
      <div className="up text-ink-500 text-center">Du sitter på</div>

      {/* Hero — Bord N · Sete N. Centred, dominant, design.md §6.10. */}
      <div
        className="mx-2 rounded-lg p-6 text-paper shadow-lg flex flex-col items-center gap-3"
        style={{
          background: 'linear-gradient(180deg, #0e2418 0%, #050d09 100%)',
        }}
      >
        <div className="flex items-baseline gap-6">
          <div className="text-center">
            <div className="text-[10px] up text-green-100/70">Bord</div>
            <div
              className="font-display font-extrabold num leading-none"
              style={{ fontSize: 72, letterSpacing: '-0.04em' }}
            >
              {seat.tableNumber}
            </div>
          </div>
          <div className="font-display text-3xl text-green-100/40">·</div>
          <div className="text-center">
            <div className="text-[10px] up text-green-100/70">Sete</div>
            <div
              className="font-display font-extrabold num leading-none"
              style={{ fontSize: 72, letterSpacing: '-0.04em' }}
            >
              {seat.seatNumber}
            </div>
          </div>
        </div>
        <div className="text-center text-[12px] text-green-100/80">
          Startstack{' '}
          <span className="font-mono num font-bold text-paper">
            {formatChipsShort(seat.startingStack)}
          </span>
        </div>
      </div>

      {/* Tablemates — names only, no live stacks. Sorted by seat number
          so players can look up from the table and match. */}
      {others.length > 0 ? (
        <div className="mx-2 bg-paper rounded-lg shadow p-4">
          <div className="up text-ink-500 mb-2">Du sitter med</div>
          <ul className="flex flex-col gap-1.5">
            {others.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between text-sm border-b border-ink-100 last:border-b-0 pb-1.5 last:pb-0"
              >
                <span className="font-semibold text-ink-900 truncate">
                  {o.shortName}
                </span>
                <span className="font-mono num text-[11px] text-ink-500 shrink-0">
                  Sete {o.seatNumber}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-center text-xs text-ink-500 px-6">
        Klokka starter når dealer sier ifra. Lykke til.
      </p>
    </section>
  )
}

export default SeatedView
